/**
 * ElderKitty — the first Cat Elder of the Scrapyard (Babylon.js).
 *
 * Per the 2026-04-14 companion book canon (p. 14), Elder Kitty is
 * "a slight black ninja" — lean, near-black longhair, quiet and
 * watchful. He teaches the "Wisdom of Grounding" and gifts the
 * Resonant Drop. Where Grand Elder Leo is a cream-tabby wizard
 * presence, Elder Kitty is compact and stealthy — an old stealthy
 * elder who arrived before anyone saw him move.
 *
 * (Note: an earlier book.html draft described Kitty as a "rugged
 * battle-scarred tomcat." The new PDF canon reverses that. The
 * tomcat GLB is kept in assets/ for historical reference but is
 * no longer referenced.)
 *
 * Appears after the Apex puzzle in a cinematic Scrapyard encounter.
 * Primarily visual — interaction is driven by the CutsceneSystem.
 *
 * Visual: near-black capsule with a cool, dim point-light (a watchful
 * presence, not a campfire, not a wizard halo), plus a subtle idle bob.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { COLORS } from "../../config/GameConfig";
import { getAssetManager, InstantiatedModel } from "../../systems/AssetManager";

// Elder Kitty model generated via Meshy per 2026-04-14 companion book
// canon (p. 14): "a slight black ninja." Lean black longhair, nicked
// ears, watchful posture, worn dark cloth wrap. ~5.2 MB GLB via the
// standard text-to-3d preview → refine PBR → remesh 20k → 1k texture
// pipeline.
const ELDER_KITTY_MODEL = "/assets/models/npcs/elder_kitty_ninja.glb";

export class ElderKitty {
  public mesh: Mesh;
  private material: StandardMaterial;
  private glowLight: PointLight;
  private baseY: number;
  private visualModel: InstantiatedModel | null = null;

  constructor(scene: Scene, position: Vector3) {
    this.baseY = position.y;

    // Body — near-black capsule (visual fallback until GLB loads).
    // Elder Kitty per new canon is a slight black ninja — the palette
    // is deep charcoal with a faint cool highlight, not warm ember.
    this.mesh = MeshBuilder.CreateCapsule(
      "elder_kitty",
      { height: 1.6, radius: 0.38, tessellation: 16, subdivisions: 6 },
      scene,
    );
    this.mesh.position = position.clone();

    this.material = new StandardMaterial("elder_kitty_mat", scene);
    // Deep near-black base with a touch of cool blue in the highlight,
    // matching "slight black ninja." Previously scorched brass (#8a5a33)
    // — that was the superseded tomcat palette.
    this.material.diffuseColor = Color3.FromHexString('#0b0b12');
    this.material.emissiveColor = new Color3(0.02, 0.02, 0.04);
    this.material.specularColor = Color3.Black();
    this.mesh.material = this.material;

    // Cool, dim watcher-presence point light — closer to moonlight on
    // dark fur than a campfire. Previously warm copper/brass; the new
    // canon's stealthy-elder framing calls for a quieter reveal.
    this.glowLight = new PointLight("elder_kitty_glow", Vector3.Zero(), scene);
    this.glowLight.diffuse = new Color3(0.45, 0.55, 0.72);
    this.glowLight.specular = new Color3(0.6, 0.7, 0.85);
    this.glowLight.intensity = 1.1;
    this.glowLight.range = 5;
    this.glowLight.parent = this.mesh;
    this.glowLight.position = new Vector3(0, 0.7, 0);

    // Fire-and-forget GLB load. Capsule stays in the graph as the
    // parent transform so the orbiting glow light and bob anim still
    // drive the authored mesh.
    getAssetManager()
      .instantiateModel(scene, ELDER_KITTY_MODEL)
      .then((model) => {
        if (this.mesh.isDisposed()) {
          model.dispose();
          return;
        }
        this.visualModel = model;
        for (const root of model.rootNodes) {
          if (root instanceof TransformNode) {
            root.parent = this.mesh;
            root.position.copyFromFloats(0, 0, 0);
            root.scaling.setAll(1.0);
            root.computeWorldMatrix(true);
            for (const mesh of model.meshes) {
              mesh.computeWorldMatrix(true);
            }
            const bb = root.getHierarchyBoundingVectors(true);
            const targetBottom = this.mesh.position.y - 0.8;
            root.position.y = targetBottom - bb.min.y;
          }
        }
        for (const m of model.meshes) m.isPickable = false;
        this.mesh.isVisible = false;
      })
      .catch((err) => {
        console.warn("[ElderKitty] Failed to load model", ELDER_KITTY_MODEL, err);
      });
  }

  /** Gentle idle bob animation — call each frame. */
  update(time: number, _deltaSeconds: number): void {
    this.mesh.position.y = this.baseY + Math.sin(time) * 0.03;

    // Tiny, nearly-still orbit — Elder Kitty arrived quietly and keeps
    // his weight centered. No theatrical swirls, no ember flicker.
    const orbitRadius = 0.35;
    this.glowLight.position.x = Math.sin(time * 0.4) * orbitRadius;
    this.glowLight.position.z = Math.cos(time * 0.4) * orbitRadius;

    // Subtle cool breathing — moonlight on dark fur, not fire
    this.glowLight.intensity = 1.0 + Math.sin(time * 1.2) * 0.15;
  }

  dispose(): void {
    this.visualModel?.dispose();
    this.visualModel = null;
    this.glowLight.dispose();
    this.material.dispose();
    this.mesh.dispose();
  }
}
